import { useState } from "react";
import { useDocumentUpload, useCreateFile } from "@swivu/client-api";
import { useQueryClient } from "@tanstack/react-query";
import {
  compressImage,
  isValidImageFile,
  createPreviewUrl,
  revokePreviewUrl
} from "../utils/imageCompression.js";

export type EntityType = "task" | "resource" | "client";

export interface EntitySelection {
  type: EntityType;
  id: number;
  name: string;
}

export interface PhotoUpload {
  file: File;
  preview: string;
  description?: string;
}

export interface UploadResult {
  success: boolean;
  fileId: string;
  error?: string;
  media?: {
    id: number;
    url: string;
    thumbnail_url: string | null;
  };
}

interface UseEntityMediaUploadProps {
  boardId: number | undefined;
  onSuccess?: () => void;
}

export const useEntityMediaUpload = ({
  boardId,
  onSuccess
}: UseEntityMediaUploadProps) => {
  const documentUpload = useDocumentUpload();
  const createFileMutation = useCreateFile();
  const queryClient = useQueryClient();

  const [uploadProgress, setUploadProgress] = useState<Map<string, number>>(
    new Map()
  );
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [isUploading, setIsUploading] = useState(false);

  const uploadPhoto = async (
    photo: PhotoUpload,
    entity: EntitySelection
  ): Promise<UploadResult> => {
    const fileId = `${photo.file.name}-${Date.now()}`;

    if (!boardId) {
      return {
        success: false,
        fileId,
        error: "No active board selected"
      };
    }

    try {
      setUploadProgress((prev) => new Map(prev).set(fileId, 0));
      setErrors((prev) => {
        const newErrors = new Map(prev);
        newErrors.delete(fileId);
        return newErrors;
      });

      // Validate file type
      if (!isValidImageFile(photo.file)) {
        throw new Error("File must be a valid image (JPEG, PNG, WebP, HEIC)");
      }

      // Validate file size (10MB limit)
      const maxSize = 10 * 1024 * 1024;
      if (photo.file.size > maxSize) {
        throw new Error("File size must be less than 10MB");
      }

      // Compress image
      setUploadProgress((prev) => new Map(prev).set(fileId, 10));
      const compressedFile = await compressImage(photo.file);
      setUploadProgress((prev) => new Map(prev).set(fileId, 30));

      // Step 1: Upload document
      const documentResponse = await documentUpload.mutateAsync({
        filename: compressedFile.name,
        file: compressedFile
      });

      setUploadProgress((prev) => new Map(prev).set(fileId, 60));

      // Step 2: Create file record linked to entity
      const fileResponse = await createFileMutation.mutateAsync({
        document: documentResponse.id,
        task: entity.type === "task" ? entity.id : null,
        resource: entity.type === "resource" ? entity.id : null,
        client: entity.type === "client" ? entity.id : null,
        description: photo.description ?? null,
        board: boardId,
        is_annotated: false
      });

      setUploadProgress((prev) => new Map(prev).set(fileId, 100));

      return {
        success: true,
        fileId,
        media: {
          id: fileResponse.id,
          url: fileResponse.url,
          thumbnail_url: fileResponse.thumbnail_url
        }
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";
      setErrors((prev) => new Map(prev).set(fileId, errorMessage));

      return {
        success: false,
        fileId,
        error: errorMessage
      };
    }
  };

  const uploadBatch = async (
    photos: PhotoUpload[],
    entity: EntitySelection
  ): Promise<UploadResult[]> => {
    setIsUploading(true);

    try {
      // Upload sequentially to avoid overwhelming the server
      const results: UploadResult[] = [];

      for (const photo of photos) {
        const result = await uploadPhoto(photo, entity);
        results.push(result);
      }

      // Invalidate queries on success
      const hasSuccess = results.some((r) => r.success);
      if (hasSuccess) {
        await queryClient.invalidateQueries({ queryKey: ["unassignedFiles"] });
        // Invalidate entity-specific files query (matches useEntityFilesSuspense key)
        await queryClient.invalidateQueries({
          queryKey: [`entity${entity.type}FilesWithSuspense`]
        });
        // Invalidate detail queries so files_count updates
        if (entity.type === "task") {
          await queryClient.invalidateQueries({ queryKey: ["taskDetail"] });
        } else if (entity.type === "resource") {
          await queryClient.invalidateQueries({ queryKey: ["resourceDetail"] });
        } else if (entity.type === "client") {
          await queryClient.invalidateQueries({ queryKey: ["clientFiles"] });
        }
        onSuccess?.();
      }

      return results;
    } finally {
      setIsUploading(false);
    }
  };

  const assignExisting = async (
    files: Array<{ id: number; document_id: number }>,
    entity: EntitySelection
  ): Promise<boolean> => {
    if (!boardId) {
      setErrors((prev) => new Map(prev).set("assign", "No active board"));
      return false;
    }

    setIsUploading(true);

    try {
      await Promise.all(
        files.map((file) =>
          createFileMutation.mutateAsync({
            document: file.document_id,
            task: entity.type === "task" ? entity.id : null,
            resource: entity.type === "resource" ? entity.id : null,
            client: entity.type === "client" ? entity.id : null,
            description: null,
            board: boardId,
            is_annotated: false
          })
        )
      );

      // Invalidate queries
      await queryClient.invalidateQueries({ queryKey: ["unassignedFiles"] });
      // Invalidate entity-specific files query (matches useEntityFilesSuspense key)
      await queryClient.invalidateQueries({
        queryKey: [`entity${entity.type}FilesWithSuspense`]
      });
      // Invalidate detail queries so files_count updates
      if (entity.type === "task") {
        await queryClient.invalidateQueries({ queryKey: ["taskDetail"] });
      } else if (entity.type === "resource") {
        await queryClient.invalidateQueries({ queryKey: ["resourceDetail"] });
      } else if (entity.type === "client") {
        await queryClient.invalidateQueries({ queryKey: ["clientFiles"] });
      }
      onSuccess?.();

      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to assign files";
      setErrors((prev) => new Map(prev).set("assign", errorMessage));
      return false;
    } finally {
      setIsUploading(false);
    }
  };

  const clearProgress = () => {
    setUploadProgress(new Map());
    setErrors(new Map());
  };

  return {
    uploadPhoto,
    uploadBatch,
    assignExisting,
    isUploading,
    uploadProgress,
    errors,
    clearProgress,
    createPreviewUrl,
    revokePreviewUrl,
    isValidImageFile
  };
};
